import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { STORAGE_KEYS } from "../../constants/storageKeys";
import storage from "../../utils/storage";
import {
  cancelVacancyThunk,
  createVacancyThunk,
  getActiveVacancies,
  getAdminVacancyHistoryThunk,
  getAllActiveCandidates,
  getAppliedCandidatesDetailsForVacancy,
  getCandidatesForRatingThunk,
  getCentreDetailsForVacancyThunk,
  getScheduledVacancies,
  getUrgentVacancies,
  getVacancyById,
  getVacancyHistory,
  rateCandidateThunk,
  selectCandidateForVacancyThunk,
  toggleCandidatePreferredStatus,
  updateVacancyThunk,
} from "./thunks";
import { IVacancyState, TCandidate, TPosition, TVacancyDetail } from "./types";

const initialState: IVacancyState = {
  selectedVacancy: null,
  inProgress: false,
  error: "",
  positions: [],
  allActiveCandidates: [],
  nonRatedCandidates: [],
  active: {
    acceptedVacancies: [],
    pendingVacancies: [],
    totalAcceptedVacancies: 0,
    totalPendingVacancies: 0,
    pendingpagination: null,
    acceptedpagination: null,
  },
  scheduled: [],
  urgent: [],
  history: {
    historyVacancies: [],
    totalHistoryVacancies: 0,
    totalScheduledVacancies: 0,
    totalUrgentVacancies: 0,
    totalAcceptedVacancies: 0,
    totalPendingVacancies: 0,
    pagination: null,
  },
  urgentVacancies: null,
  scheduledVacancies: null,
  historyVacancies: null,
  allAppliedCandidatesList: null,
  selectedCentreDetails: null,
  allCentreDetailsList: [],
  totalUrgentVacancies: 0,
  totalScheduledVacancies: 0,
  totalAcceptedVacancies: 0,
};

const vacancySlice = createSlice({
  name: "vacancy",
  initialState,
  reducers: {
    resetVacancyErrorAction: (state) => {
      state.error = null;
    },
    getAllPositionsListAction(state, action: PayloadAction<TPosition[]>) {
      state.positions = action.payload;
    },
    getAllActiveCandidatesListAction(
      state,
      action: PayloadAction<{ candidates: TCandidate[] }>
    ) {
      state.allActiveCandidates = action.payload.candidates;
    },
    resetAllActiveCandidatesAction(state) {
      state.allActiveCandidates = [];
    },
    setUrgentVacanciesAction(state, action) {
      state.urgentVacancies = action.payload;
    },
    setScheduledVacanciesAction(state, action) {
      state.scheduledVacancies = action.payload;
    },
    setHistoryVacanciesAction(state, action) {
      state.historyVacancies = action.payload;
    },
    setSelectedCentreDetailsAction: (state, action: PayloadAction<any>) => {
      state.selectedCentreDetails = action.payload?.uuid ?? null;
      storage.set(STORAGE_KEYS.SELECTED_CENTRE_ID, action.payload?.id ?? 0);
      storage.set(
        STORAGE_KEYS.SELECTED_CENTRE_UUID,
        action.payload?.uuid ?? null
      );
    },
    resetSelectedCentreDetails: (state) => {
      state.selectedCentreDetails = null;
      storage.remove(STORAGE_KEYS.SELECTED_CENTRE_ID);
      storage.remove(STORAGE_KEYS.SELECTED_CENTRE_UUID);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createVacancyThunk.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(createVacancyThunk.fulfilled, (state) => {
      state.inProgress = false;
    });
    builder.addCase(createVacancyThunk.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });

    builder.addCase(getVacancyById.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(getVacancyById.fulfilled, (state, action) => {
      state.inProgress = false;
      state.selectedVacancy = action.payload.vacancy;
      // state.allAppliedCandidatesList = action.payload.vacancy.vacancy_applications
    });
    builder.addCase(getVacancyById.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });

    builder.addCase(updateVacancyThunk.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(updateVacancyThunk.fulfilled, (state, action) => {
      state.inProgress = false;
      state.selectedVacancy = action.payload.vacancy;
    });
    builder.addCase(updateVacancyThunk.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });

    builder.addCase(getActiveVacancies.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(getActiveVacancies.fulfilled, (state, action) => {
      state.inProgress = false;
      state.active.pendingVacancies = action.payload.pending_vacancy.data;
      state.active.totalPendingVacancies = action.payload.total_pending_vacancy;
      state.active.acceptedVacancies = action.payload.accepted_vacancy.data;
      state.totalUrgentVacancies = action.payload.total_urgent_vacancies ?? 0;
      state.totalScheduledVacancies = action.payload.total_scheduled_vacancies ?? 0;
      state.totalAcceptedVacancies = action.payload.total_accepted_vacancy ?? 0;
      state.active.totalAcceptedVacancies =
        action.payload.total_accepted_vacancy;
      state.active.pendingpagination = {
        current_page: action.payload.pending_vacancy.current_page,
        per_page: action.payload.pending_vacancy.per_page,
        total: action.payload.pending_vacancy.total,
        last_page: action.payload.pending_vacancy.last_page,
        next_page_url: action.payload.pending_vacancy.next_page_url,
        prev_page_url: action.payload.pending_vacancy.prev_page_url,
        links: action.payload.pending_vacancy.links,
      };
      state.active.acceptedpagination = {
        current_page: action.payload.accepted_vacancy.current_page,
        per_page: action.payload.accepted_vacancy.per_page,
        total: action.payload.accepted_vacancy.total,
        last_page: action.payload.accepted_vacancy.last_page,
        next_page_url: action.payload.accepted_vacancy.next_page_url,
        prev_page_url: action.payload.accepted_vacancy.prev_page_url,
        links: action.payload.accepted_vacancy.links,
      };
    });
    builder.addCase(getActiveVacancies.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });
    builder.addCase(toggleCandidatePreferredStatus.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(
      toggleCandidatePreferredStatus.fulfilled,
      (state, action) => {
        state.inProgress = false;
        const { user_id, centre_id, is_preferred } = action.meta.arg;

        // Helper to update preferred status in a vacancy list
        const updatePreferredStatus = (vacancyList: TVacancyDetail[]) => {
          for (const vacancy of vacancyList) {
            if (
              vacancy.centre_id === centre_id &&
              vacancy.selected_candidate?.id === user_id
            ) {
              vacancy.selected_candidate.is_preferred_candidate = is_preferred;
              break;
            }
          }
        };

        updatePreferredStatus(state.active.acceptedVacancies || []);
        updatePreferredStatus(state.active.pendingVacancies || []);
        updatePreferredStatus(state.history.historyVacancies || []);
      }
    );
    builder.addCase(
      toggleCandidatePreferredStatus.rejected,
      (state, action) => {
        state.inProgress = false;
        state.error = action.payload;
      }
    );
    builder.addCase(rateCandidateThunk.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(rateCandidateThunk.fulfilled, (state, action) => {
      state.inProgress = false;

      // Loop through each rated candidate in the payload
      action.meta.arg.forEach(
        ({ centre_id, rating, candidate_id, vacancy_id }) => {
          const index = state.active.acceptedVacancies.findIndex(
            (vacancy) =>
              vacancy.id === vacancy_id &&
              vacancy.centre_id === centre_id &&
              vacancy.selected_candidate?.id === candidate_id
          );

          if (index !== -1) {
            state.active.acceptedVacancies[index] = {
              ...state.active.acceptedVacancies[index],
              selected_candidate: {
                ...state.active.acceptedVacancies[index].selected_candidate,
                rating: {
                  latest_rating: rating as number,
                  average_rating: rating as number,
                },
              },
            };
          }
        }
      );
    });
    builder.addCase(rateCandidateThunk.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });
    builder.addCase(getCandidatesForRatingThunk.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(getCandidatesForRatingThunk.fulfilled, (state, action) => {
      state.inProgress = false;
      state.nonRatedCandidates = action.payload.data?.applications || [];
    });
    builder.addCase(getCandidatesForRatingThunk.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });
    builder.addCase(getScheduledVacancies.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(getScheduledVacancies.fulfilled, (state, action) => {
      state.inProgress = false;
      // state.scheduled = action.payload;
      state.totalUrgentVacancies = action.payload.total_urgent_vacancies ?? 0;
      state.totalScheduledVacancies = action.payload.total_scheduled_vacancies ?? 0;
      state.totalAcceptedVacancies = action.payload.total_accepted_vacancies ?? 0;
    });
    builder.addCase(getScheduledVacancies.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });

    builder.addCase(getUrgentVacancies.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(getUrgentVacancies.fulfilled, (state, action) => {
      state.inProgress = false;
      state.totalUrgentVacancies = action.payload.total_urgent_vacancies ?? 0;
      state.totalScheduledVacancies = action.payload.total_scheduled_vacancies ?? 0;
      state.totalAcceptedVacancies = action.payload.total_accepted_vacancies ?? 0;
    });
    builder.addCase(getUrgentVacancies.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });

    builder.addCase(getVacancyHistory.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(getVacancyHistory.fulfilled, (state, action) => {
      state.inProgress = false;
      state.history.historyVacancies = action.payload.history_vacancies.data;
      state.history.totalPendingVacancies =
        action.payload.total_pending_vacancy;
      state.history.totalAcceptedVacancies =
        action.payload.total_accepted_vacancy;
      state.history.pagination = {
        current_page: action.payload.history_vacancies.current_page,
        per_page: action.payload.history_vacancies.per_page,
        total: action.payload.history_vacancies.total,
        last_page: action.payload.history_vacancies.last_page,
        next_page_url: action.payload.history_vacancies.next_page_url,
        prev_page_url: action.payload.history_vacancies.prev_page_url,
        links: action.payload.history_vacancies.links,
      };
    });
    builder.addCase(getVacancyHistory.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });

    builder.addCase(selectCandidateForVacancyThunk.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(
      selectCandidateForVacancyThunk.fulfilled,
      (state, action) => {
        state.inProgress = false;
      }
    );
    builder.addCase(
      selectCandidateForVacancyThunk.rejected,
      (state, action) => {
        state.inProgress = false;
        state.error = action.payload;
      }
    );

    builder.addCase(getAppliedCandidatesDetailsForVacancy.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(
      getAppliedCandidatesDetailsForVacancy.fulfilled,
      (state, action) => {
        state.inProgress = false;
        state.allAppliedCandidatesList = action.payload.application;
      }
    );
    builder.addCase(
      getAppliedCandidatesDetailsForVacancy.rejected,
      (state, action) => {
        state.inProgress = false;
        state.error = action.payload;
      }
    );

    builder.addCase(getAdminVacancyHistoryThunk.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(getAdminVacancyHistoryThunk.fulfilled, (state, action) => {
      state.inProgress = false;
      state.error = null;
      state.totalUrgentVacancies = action.payload.total_urgent_vacancies ?? 0;
      state.totalScheduledVacancies = action.payload.total_scheduled_vacancies ?? 0;
      state.totalAcceptedVacancies = action.payload.total_accepted_vacancy ?? 0;
    });
    builder.addCase(getAdminVacancyHistoryThunk.rejected, (state) => {
      state.inProgress = false;
      state.error = null;
    });
    builder.addCase(cancelVacancyThunk.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(cancelVacancyThunk.fulfilled, (state, action) => {
      const cancelledVacancy = action.payload;
      const uuid = cancelledVacancy.uuid;
      const { active_vacancy_type } = action.meta.arg;

      if (active_vacancy_type === "pending") {
        state.active.pendingVacancies = state.active.pendingVacancies.filter(
          (vacancy) => vacancy.uuid !== uuid
        );
        state.active.totalPendingVacancies -= 1;
      }

      if (active_vacancy_type === "accepted") {
        state.active.acceptedVacancies = state.active.acceptedVacancies.filter(
          (vacancy) => vacancy.uuid !== uuid
        );
        state.active.totalAcceptedVacancies -= 1;
      }
    });
    builder.addCase(cancelVacancyThunk.rejected, (state) => {
      state.inProgress = false;
      state.error = null;
    });

    builder.addCase(getCentreDetailsForVacancyThunk.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(
      getCentreDetailsForVacancyThunk.fulfilled,
      (state, action) => {
        state.inProgress = false;
        state.allCentreDetailsList = action.payload.centres;
      }
    );
    builder.addCase(getCentreDetailsForVacancyThunk.rejected, (state) => {
      state.inProgress = false;
      state.error = null;
    });

    builder.addCase(getAllActiveCandidates.pending, (state) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(getAllActiveCandidates.fulfilled, (state, action) => {
      state.inProgress = false;
      state.allActiveCandidates = action.payload.data?.candidates || [];
    });
    builder.addCase(getAllActiveCandidates.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });
  },
});

export const {
  resetVacancyErrorAction,
  getAllPositionsListAction,
  getAllActiveCandidatesListAction,
  resetAllActiveCandidatesAction,
  setUrgentVacanciesAction,
  setHistoryVacanciesAction,
  setScheduledVacanciesAction,
  setSelectedCentreDetailsAction,
  resetSelectedCentreDetails,
} = vacancySlice.actions;

export default vacancySlice.reducer;
